import { api } from '../lib/api'

export type TreatmentPayment = {
  readonly id: number
  readonly date: string | null
  readonly price: number
  readonly note: string | null
}

export type Treatment = {
  readonly id: number
  readonly date: string | null
  readonly name: string | null
  readonly code: string | null
  readonly description: string | null
  readonly note: string | null
  readonly timeless: boolean | null
  readonly fee: number | null
  readonly price: number
  readonly paid: number
  readonly price_per_fee: number
  readonly deletable: boolean
  readonly printable_html: string | null
  readonly payments: readonly TreatmentPayment[]
}

export type ProgramTreatmentLine = {
  readonly name: string | null
  readonly price: string | null
  readonly note: string | null
  readonly timeless: boolean | null
}

export type ProgramOfTreatment = {
  readonly id: number
  readonly date: string | null
  readonly name: string | null
  readonly description: string | null
  readonly price: number
  readonly paid: number
  readonly price_per_fee: number | null
  readonly deletable: boolean
  readonly printable_html: string | null
  readonly treatments: readonly ProgramTreatmentLine[]
  readonly payments: readonly TreatmentPayment[]
}

export type TreatmentsIndex = {
  readonly patient: { readonly id: string; readonly name: string | null }
  readonly treatments: readonly Treatment[]
  readonly program_of_treatments: readonly ProgramOfTreatment[]
}

export type TreatmentRecord = {
  readonly id: number
  readonly name: string | null
  readonly code: string | null
  readonly price: string | null
  readonly description: string | null
  readonly note: string | null
  readonly fee: number | null
  readonly timeless: boolean | null
  readonly date: string | null
  readonly patient_id: number
}

export type TreatmentAttributes = {
  readonly date?: string
  readonly name?: string
  readonly code?: string
  readonly price?: string
  readonly description?: string
  readonly note?: string
  readonly fee?: string
  readonly timeless?: boolean
}

export type TreatmentTemplate = {
  readonly id: number
  readonly name: string | null
  readonly code: string | null
  readonly price: string | null
  readonly description: string | null
  readonly note: string | null
  readonly timeless: boolean | null
  readonly health_center_id: number
}

export type TreatmentTemplateAttributes = {
  readonly name?: string
  readonly code?: string
  readonly price?: string
  readonly description?: string
  readonly note?: string
  readonly timeless?: boolean
}

export type ProgramOfTreatmentAttributes = {
  readonly date: string
  readonly name: string
  readonly description: string
  readonly treatments: readonly string[]
  readonly treatments_price: readonly string[]
  readonly treatments_note: readonly string[]
}

function treatmentsPath(healthCenterId: number, patientId: string): string {
  return `/health_centers/${healthCenterId}/patients/${patientId}/treatments`
}

export function fetchTreatments(healthCenterId: number, patientId: string): Promise<TreatmentsIndex> {
  return api.get<TreatmentsIndex>(`${treatmentsPath(healthCenterId, patientId)}.json`)
}

export function fetchTreatment(healthCenterId: number, patientId: string, id: string): Promise<TreatmentRecord> {
  return api.get<TreatmentRecord>(`${treatmentsPath(healthCenterId, patientId)}/${id}.json`)
}

export function createTreatment(healthCenterId: number, patientId: string, treatment: TreatmentAttributes): Promise<TreatmentRecord> {
  return api.post<TreatmentRecord>(`${treatmentsPath(healthCenterId, patientId)}.json`, { treatment })
}

export function updateTreatment(
  healthCenterId: number,
  patientId: string,
  id: string,
  treatment: TreatmentAttributes,
): Promise<TreatmentRecord> {
  return api.patch<TreatmentRecord>(`${treatmentsPath(healthCenterId, patientId)}/${id}.json`, { treatment })
}

export function deleteTreatment(healthCenterId: number, patientId: string, id: number): Promise<void> {
  return api.delete<void>(`${treatmentsPath(healthCenterId, patientId)}/${id}.json`)
}

export function createTreatmentPayment(
  healthCenterId: number,
  patientId: string,
  treatmentId: number,
  payment: { date: string; price: string; note: string },
): Promise<{ redirect: string }> {
  return api.post<{ redirect: string }>(`${treatmentsPath(healthCenterId, patientId)}/${treatmentId}/treatment_payments`, payment)
}

export function createProgramPayment(
  healthCenterId: number,
  patientId: string,
  programId: number,
  payment: { date: string; price: string; note: string },
): Promise<{ redirect: string }> {
  return api.post<{ redirect: string }>(
    `${treatmentsPath(healthCenterId, patientId)}/${programId}/treatment_payments_for_program`,
    payment,
  )
}

export function createProgramOfTreatment(
  healthCenterId: number,
  patientId: string,
  programOfTreatment: ProgramOfTreatmentAttributes,
): Promise<{ redirect: string }> {
  return api.post<{ redirect: string }>(`/health_centers/${healthCenterId}/patients/${patientId}/program_of_treatments`, {
    program_of_treatment: programOfTreatment,
  })
}

export function deleteProgramOfTreatment(healthCenterId: number, patientId: string, id: number): Promise<void> {
  return api.delete<void>(`/health_centers/${healthCenterId}/patients/${patientId}/program_of_treatments/${id}`)
}

export function fetchTreatmentTemplates(healthCenterId: number): Promise<readonly TreatmentTemplate[]> {
  return api.get<readonly TreatmentTemplate[]>(`/health_centers/${healthCenterId}/treatment_templates.json`)
}

export function fetchTreatmentTemplate(healthCenterId: number, id: string): Promise<TreatmentTemplate> {
  return api.get<TreatmentTemplate>(`/health_centers/${healthCenterId}/treatment_templates/${id}.json`)
}

export function createTreatmentTemplate(healthCenterId: number, treatmentTemplate: TreatmentTemplateAttributes): Promise<TreatmentTemplate> {
  return api.post<TreatmentTemplate>(`/health_centers/${healthCenterId}/treatment_templates.json`, { treatment_template: treatmentTemplate })
}

export function updateTreatmentTemplate(
  healthCenterId: number,
  id: string,
  treatmentTemplate: TreatmentTemplateAttributes,
): Promise<TreatmentTemplate> {
  return api.patch<TreatmentTemplate>(`/health_centers/${healthCenterId}/treatment_templates/${id}.json`, {
    treatment_template: treatmentTemplate,
  })
}

export function deleteTreatmentTemplate(healthCenterId: number, id: number): Promise<void> {
  return api.delete<void>(`/health_centers/${healthCenterId}/treatment_templates/${id}.json`)
}
